import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  IconButton,
  Grid,
  Alert,
  Box,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import { useTranslation } from 'react-i18next';
import { useAuth, Branch } from '../../../../contexts/AuthContext';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import salesService, { PackageScreenDto } from '../../../../services/salesService';

// ✅ PackageScreen metodları salesService'e eklendi

interface AddPackageScreenModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  // Düzenleme modu için props
  editMode?: boolean;
  packageScreenId?: number;
  quickSaleScreenId?: number;
  screenName?: string;
  screenBranchId?: string | null;
  onSaved?: () => void;
}

const AddPackageScreenModal: React.FC<AddPackageScreenModalProps> = ({ 
  open, 
  onClose, 
  companyId,
  editMode = false,
  packageScreenId,
  quickSaleScreenId,
  screenName: initialScreenName,
  screenBranchId,
  onSaved
}) => {
  const { t } = useTranslation('salesSystem');
  const { branches } = useAuth();
  const { colors: themeColors } = useThemeContext(); // ✅ ThemeContext'ten renkler
  const [screenName, setScreenName] = useState('');
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // ✅ ŞUBE TUTARLILIK için mevcut paket ekranları
  const [existingScreens, setExistingScreens] = useState<PackageScreenDto[] | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(false);

  // Modal açıldığında/kapandığında state'leri sıfırla veya yükle
  useEffect(() => {
    if (!open || !companyId) {
      setScreenName('');
      setSelectedBranch(null);
      setError(null);
      setExistingScreens(null);
      return;
    }

    // Düzenleme modunda mevcut verileri yükle
    if (editMode) {
      setScreenName(initialScreenName || '');
      if (screenBranchId) {
        const branch = branches.find(b => b.id === screenBranchId);
        setSelectedBranch(branch || null);
      } else {
        setSelectedBranch(null);
      }
    } else {
      // Yeni ekran modu - varsayılan değerler
      setScreenName('');
      setSelectedBranch(null);
    }

    // ✅ Mevcut paket ekranlarını al (şube politikası için)
    let cancelled = false;
    setLoadingExisting(true);
    salesService.getPackageScreens()
      .then((list) => {
        if (!cancelled) {
          setExistingScreens(list || []);
        }
      })
      .catch((err) => {
        console.error('[AddPackageScreen] Mevcut ekranlar yüklenemedi:', err);
        if (!cancelled) {
          // Yüklenmezse kullanıcıyı blok etmemek için boş listeyle devam et;
          // backend zaten 400 ile son söz hakkını tutuyor.
          setExistingScreens([]);
          setError(t('orderManagement.addPackageScreen.errors.loadFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingExisting(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, companyId, editMode, initialScreenName, screenBranchId, branches, t]);

  // ✅ Şube politikası türevleri
  const branchPolicy = useMemo(() => {
    if (!existingScreens) {
      return {
        ready: false,
        hasBranchedScreen: false,
        hasUnbranchedScreen: false,
        usedBranchIds: new Set<string>(),
      };
    }

    // Düzenleme modunda kendimizi sayma
    const others = editMode && packageScreenId
      ? existingScreens.filter(s => s.Id !== packageScreenId)
      : existingScreens;

    const branched = others.filter(s => !!s.BranchId);
    const unbranched = others.filter(s => !s.BranchId);
    const usedBranchIds = new Set<string>(
      branched.map(s => String(s.BranchId))
    );

    return {
      ready: true,
      hasBranchedScreen: branched.length > 0,
      hasUnbranchedScreen: unbranched.length > 0,
      usedBranchIds,
    };
  }, [existingScreens, editMode, packageScreenId]);

  // ✅ Bu modal için validation sonucu
  const policyError: string | null = useMemo(() => {
    if (!branchPolicy.ready) return null;

    // CREATE
    if (!editMode) {
      // Şube zorunlu mu?
      if (branchPolicy.hasBranchedScreen && !selectedBranch) {
        return t(
          'orderManagement.addPackageScreen.validation.branchRequired',
          'Bu şirkette şubeli paket ekranı tanımlı. Yeni ekran için şube seçimi zorunludur.'
        );
      }
      // Şubesiz ekran varken yenisi (şubeli veya şubesiz) eklenemez
      if (branchPolicy.hasUnbranchedScreen && !branchPolicy.hasBranchedScreen) {
        return t(
          'orderManagement.addPackageScreen.validation.unbranchedExists',
          'Bu şirkette şubesiz bir paket ekranı var. İkinci paket ekranı eklemek için önce mevcut ekranı bir şubeye atayın.'
        );
      }
      // Seçilen şube zaten kullanımda mı?
      if (selectedBranch && branchPolicy.usedBranchIds.has(String(selectedBranch.id))) {
        return t(
          'orderManagement.addPackageScreen.validation.branchAlreadyUsed',
          'Bu şubeye zaten bir paket ekranı atanmış. Her şubeye yalnızca tek paket ekranı tanımlanabilir.'
        );
      }
      return null;
    }

    // EDIT
    // Mevcut ekran şubeliydi, kullanıcı şubeyi temizliyor → diğer şubeli ekranlar varsa reddet
    const wasBranched = !!screenBranchId;
    if (wasBranched && !selectedBranch && branchPolicy.hasBranchedScreen) {
      return t(
        'orderManagement.addPackageScreen.validation.cannotUnsetBranch',
        'Bu şirkette başka şubeli paket ekranları olduğu için bu ekran şubesiz hale getirilemez.'
      );
    }
    // Hedef şube zaten başka bir ekranda kullanılıyorsa
    if (selectedBranch && branchPolicy.usedBranchIds.has(String(selectedBranch.id))) {
      return t(
        'orderManagement.addPackageScreen.validation.branchAlreadyUsed',
        'Bu şubeye zaten bir paket ekranı atanmış. Her şubeye yalnızca tek paket ekranı tanımlanabilir.'
      );
    }
    return null;
  }, [branchPolicy, editMode, selectedBranch, screenBranchId, t]);

  const branchFieldRequired = !editMode && branchPolicy.hasBranchedScreen;

  const handleSave = async () => {
    if (!screenName.trim()) {
      setError(t('orderManagement.addPackageScreen.errors.screenNameRequired'));
      return;
    }

    if (!companyId) {
      setError(t('orderManagement.addPackageScreen.errors.companyMissing'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (editMode && packageScreenId && quickSaleScreenId) {
        // ✅ Güncelleme modu - mevcut ekranları güncelle
        console.log("🔄 Paket Ekranı güncelleniyor...");
        
        // 1. QuickSaleScreen'i güncelle
        await salesService.updateQuickSaleScreen({
          Id: quickSaleScreenId,
          Name: screenName,
          BranchId: selectedBranch?.id || null,
        });

        // 2. PackageScreen'i güncelle
        await salesService.updatePackageScreen({
          Id: packageScreenId,
          Name: screenName,
          BranchId: selectedBranch?.id || null,
        });

        console.log("✅ Paket Ekranı güncellendi");
        
        if (onSaved) {
          onSaved();
        }
        handleClose();
      } else {
        // ✅ Yeni ekran oluşturma modu
        console.log("🆕 Yeni Paket Ekranı oluşturuluyor...");

        // 1. Önce QuickSaleScreen oluştur (ScreenType='Package')
        // PackageScreenId daha sonra güncellenecek
        const quickSaleResponse = await salesService.createQuickSaleScreen({
          Name: screenName,
          BranchId: selectedBranch?.id || null,
          ScreenType: 'Package', // ✅ Paket ekranı olduğunu belirtiyoruz
          AssignedPersonnelJson: null,
          PaymentOptionsJson: null,
          StockVisibilityJson: null,
          PrintingSettingsJson: null,
          PersonnelPermissionsJson: null,
          PrepaidSystemEnabled: false,
          ShowProductImages: true,
          UseFiscalDeviceForCash: false,
          AllowAccountCreationOnSale: false
        });

        console.log("✅ QuickSaleScreen oluşturuldu:", quickSaleResponse.Id);

        // 2. PackageScreen oluştur (QuickSaleScreenId ile)
        const packageResponse = await salesService.createPackageScreen({
          Name: screenName,
          BranchId: selectedBranch?.id || null,
          QuickSaleScreenId: quickSaleResponse.Id,
          DefaultDeliveryTime: 45,
          AutoPrintKitchen: false,
          ShowCallerIdPanel: true,
        });

        console.log("✅ PackageScreen oluşturuldu:", packageResponse.Id);

        // 3. QuickSaleScreen'i güncelle - PackageScreenId'yi bağla
        await salesService.updateQuickSaleScreen({
          Id: quickSaleResponse.Id,
          PackageScreenId: packageResponse.Id
        });

        console.log("✅ QuickSaleScreen güncellendi - PackageScreenId bağlandı");
        console.log("🎉 Yeni Paket Ekranı başarıyla oluşturuldu!");
        
        if (onSaved) {
          onSaved();
        }
        handleClose();
      }

    } catch (err: any) {
      console.error("Paket ekranı kaydedilirken hata:", err);
      setError(err.response?.data?.Message || t('orderManagement.addPackageScreen.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setScreenName('');
    setSelectedBranch(null);
    setError(null);
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: themeColors.primary,
        color: 'white'
      }}>
        {editMode ? t('orderManagement.addPackageScreen.title.edit') : t('orderManagement.addPackageScreen.title.create')}
        <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        {loadingExisting && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              {t('orderManagement.addPackageScreen.validation.loading', 'Mevcut paket ekranları kontrol ediliyor...')}
            </Typography>
          </Box>
        )}

        {policyError && !loadingExisting && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {policyError}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Ekran Adı */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('orderManagement.addPackageScreen.fields.screenName')}
              value={screenName}
              onChange={(e) => setScreenName(e.target.value)}
              required
              InputProps={{
                startAdornment: <LocalShippingIcon color="action" sx={{ mr: 1 }} />
              }}
              helperText={t('orderManagement.addPackageScreen.fields.screenNameHelper')}
              autoFocus
            />
          </Grid>

          {/* Atanan Şube */}
          <Grid item xs={12}>
            <Autocomplete
              options={branches
                .filter(b => b.id !== 'null')
                // Düzenleme modunda kendi şubemiz exclude edilmez (zaten policy hesabında çıkarılıyor)
                .filter(b => !branchPolicy.usedBranchIds.has(String(b.id)))}
              getOptionLabel={(option) => option.name}
              value={selectedBranch}
              onChange={(_, newValue) => setSelectedBranch(newValue)}
              noOptionsText={
                branchPolicy.hasBranchedScreen
                  ? t('orderManagement.addPackageScreen.validation.branchAlreadyUsed', 'Bu şubeye zaten bir paket ekranı atanmış. Her şubeye yalnızca tek paket ekranı tanımlanabilir.')
                  : undefined
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  required={branchFieldRequired}
                  label={branchFieldRequired
                    ? t('orderManagement.addPackageScreen.fields.branchRequired', 'Atanan Şube')
                    : t('orderManagement.addPackageScreen.fields.branch')}
                  helperText={branchFieldRequired
                    ? t('orderManagement.addPackageScreen.fields.branchHelperRequired', 'Bu şirkette şubeli paket ekranı tanımlı; şube seçimi zorunludur.')
                    : t('orderManagement.addPackageScreen.fields.branchHelper')}
                  error={branchFieldRequired && !selectedBranch}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <BusinessIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          {t('orderManagement.addPackageScreen.buttons.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ bgcolor: themeColors.primary, '&:hover': { bgcolor: themeColors.primary, opacity: 0.9 } }}
          disabled={
            !screenName.trim()
            || saving
            || loadingExisting
            || !!policyError
          }
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {saving
            ? t('orderManagement.addPackageScreen.buttons.saving')
            : (editMode
              ? t('orderManagement.addPackageScreen.buttons.update')
              : t('orderManagement.addPackageScreen.buttons.create'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPackageScreenModal;
